/**
 * StatusBadge — Layer-1 glanceable status display.
 *
 * Contract (non-negotiable):
 *   - Icon + word + color, always all three. No icon-only mode.
 *   - Color is never the sole channel (WCAG AA). Icon + word carry meaning too.
 *   - Consumes SWE-1's <Badge variant="success|warning|danger|info|neutral"/>.
 *     If variants aren't published yet, we degrade to a local className map so
 *     existing pages keep working.
 *
 * Devil's advocate rebuttals baked in:
 *   - "Why show the word on a 500-row RDP portfolio? Use icon-only." Because a
 *     column of 500 identical-ish icons is meaningless. The word is information
 *     density; `compact` shrinks the word but keeps it.
 *   - "Why not aria-hide the icon?" Because on mobile / high density the word
 *     can clip; the icon then carries the ownership hint. Icon has its own
 *     aria-label-less render; screen readers read the text badge content.
 */

import { type HTMLAttributes } from "react";
import { Badge } from "@/components/shared/ui/badge";
import { cn } from "@/lib/utils";
import {
  getStatusDisplay,
  type BadgeTone,
  type Jurisdiction,
} from "./statusTaxonomy";

interface StatusBadgeProps extends HTMLAttributes<HTMLDivElement> {
  code: number;
  jurisdiction: Jurisdiction;
  /** Compact tables (RDP portfolio, staff submissions list). Still shows word. */
  compact?: boolean;
}

// Fallback tone styles for the case SWE-1's Badge variants aren't live yet.
// These are intentionally high-contrast so color is never the sole channel,
// but the icon + word are also always rendered.
const TONE_FALLBACK: Record<BadgeTone, string> = {
  success: "bg-green-100 text-green-900 border-green-300",
  warning: "bg-amber-100 text-amber-900 border-amber-300",
  danger: "bg-red-100 text-red-900 border-red-300",
  info: "bg-blue-100 text-blue-900 border-blue-300",
  neutral: "bg-gray-100 text-gray-900 border-gray-300",
};

// Map our tones onto SWE-1's planned CVA variants. Once `badge.tsx` ships
// success/warning/danger/info/neutral, this is the only line that changes.
const TONE_TO_VARIANT: Record<BadgeTone, string> = {
  success: "success",
  warning: "warning",
  danger: "danger",
  info: "info",
  neutral: "neutral",
};

export function StatusBadge({
  code,
  jurisdiction,
  compact = false,
  className,
  ...rest
}: StatusBadgeProps) {
  const display = getStatusDisplay(code, { jurisdiction });
  const Icon = display.icon;
  const label = compact ? display.shortLabel : display.label;
  const variant = TONE_TO_VARIANT[display.tone];

  // TODO(SWE-1): remove `TONE_FALLBACK` override once badge.tsx exposes
  // success/warning/danger/info/neutral CVA variants. `variant` above will
  // then be honored directly without className layering.
  return (
    <Badge
      // `variant` is declared above as any-string because SWE-1's Badge
      // hasn't merged its extended variants yet. Cast keeps us typesafe
      // once it does without changing callers.
      variant={variant as never}
      className={cn(
        "gap-1.5",
        compact ? "text-[0.7rem] px-2 py-0.5" : "text-xs",
        TONE_FALLBACK[display.tone],
        className,
      )}
      role="status"
      aria-label={`Filing status: ${display.label}`}
      {...rest}
    >
      <Icon
        className={cn(compact ? "h-3 w-3" : "h-3.5 w-3.5")}
        aria-hidden="true"
      />
      <span>{label}</span>
    </Badge>
  );
}

export default StatusBadge;
